import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import MobileListCard from '../components/MobileListCard';
import EmptyState from '../components/EmptyState';
import moment from 'moment';

const CircularScreen = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadData(1);
  }, []);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/circular/get-circular-list-student?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newData = response.data.data || [];

      if (page === 1) {
        setItems(newData);
      } else {
        setItems((prev) => [...prev, ...newData]);
      }

      setPageNumber(page + 1);
      setHasMore(newData.length === 10);
    } catch (error) {
      console.error('CircularScreen loadData error:', error);
      handleCatch(error);
      if (page === 1) setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setPageNumber(1);
    setHasMore(true);
    await loadData(1);
  }, []);

  const onDownload = (fileUrl) => {
    if (!fileUrl) {
      alert('No file available to download');
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Circulars"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<DescriptionIcon />}
            iconColor="#00ACC1"
            title="No Circulars"
            subtitle="No circulars have been posted yet."
            actionLabel="Retry"
            onAction={() => loadData(1)}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              mt: 1,
              mx: 1,
              borderRadius: '12px 12px 0 0',
              overflow: 'hidden',
            }}
          >
            {items.map((item, index) => (
              <MobileListCard
                key={index}
                icon={<DescriptionIcon />}
                iconColor="#00ACC1"
                iconBg="rgba(0,172,193,0.1)"
                title={item.title}
                subtitle={`${item.uploadedBy || ''}  •  ${moment(item.updatedDate).format('DD MMM YYYY')}`}
                description={item.description}
                rightIcon={item.file1 ? <DownloadIcon /> : undefined}
                onClick={item.file1 ? () => onDownload(item.file1) : undefined}
                showDivider={index < items.length - 1}
                showChevron={false}
              />
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box
                    onClick={loadMore}
                    sx={{
                      fontSize: 14,
                      color: colors.BLUE,
                      cursor: 'pointer',
                      fontWeight: 500,
                      py: 1,
                    }}
                  >
                    Load more
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CircularScreen;
